<script lang="ts">
  import Tabs from "$lib/Tabs.svelte";
  import { page } from "$app/state";
  import Button from "$lib/Button.svelte";
  import { onMount } from "svelte";
  import lang, { additionalLangsRef as additionalLanguages } from "$lib/langStore";
  import PortableTextContent from "$lib/portableText/PortableTextContent.svelte";
  import CustomerserviceBanner from "$lib/CustomerServiceBanner.svelte";
  import CoverPicture from "$lib/CoverPicture.svelte";
  import TeaserSection from "$lib/TeaserSection.svelte";
  import type { LayoutData } from "./$types";
  import Breadcrumb from "$lib/Breadcrumb.svelte";
  import LanguagePicker from "$lib/SubLayoutLanguagePicker.svelte";

  const tabLayoutUrls = [
    ...["", ...Object.values(additionalLanguages).map((s) => `/${s}`)].map(
      (add) => `${add}/avtale`
    ),
    ...["", ...Object.values(additionalLanguages).map((s) => `/${s}`)].map(
      (add) => `${add}/avtale/fastpris`
    ),
  ];

  interface Props {
    data: LayoutData;
    children?: import("svelte").Snippet;
  }

  let { data, children }: Props = $props();

  let search = $state("");
  let leadContent = $derived(data.page?.lead ? data.page.lead : null);
  let serviceTitle = $derived(
    $lang === "en"
      ? "Other questions? Feel free to contact us"
      : $lang === "se"
        ? "Jus leat eará gažaldagat, de váldde áinnas oktavuođa minguin"
        : null
  );
  let servicePhoneLabel = $derived(
    $lang === "en" ? "Call us at" : $lang === "se" ? "Riŋge midjiide" : null
  );
  let serviceEmailLabel = $derived(
    $lang === "en" ? "Send us an email at" : $lang === "se" ? "Dahje sádde e-poasta" : null
  );
  let app_teaser = $derived(data?.app_teaser);

  onMount(() => {
    if (page.url?.search) search = page.url.search;
  });
</script>

<Breadcrumb breadcrumbs={page.data.breadcrumbs} />

{#if tabLayoutUrls.includes(page.url.pathname)}
  <LanguagePicker href="/avtale">
    <CoverPicture image={data.page?.image ? data.page.image : null} />

    <p class="mx-edge-x mb-5 heading-1">{data.page?.title || `Strømavtaler`}</p>

    {#if !!$lang}
      {#if leadContent}
        <div class="[--text-body:var(--text-lead)]">
          <PortableTextContent content={leadContent} />
        </div>
      {/if}

      <!-- TODO: remove lang when we have sami labels -->
      <CustomerserviceBanner
        class="my-10"
        contactForm={false}
        title={serviceTitle}
        phoneLabel={servicePhoneLabel}
        emailLabel={serviceEmailLabel}
      >
        {#if $lang === "se"}
          <p class="mt-0 italic">
            <!-- Information that no one at customer service speaks Sami. -->
            Min geavaheaddjiguovddáš sáhttá veahkehit du dáro- ja eŋgelasgillii.
          </p>
        {/if}
      </CustomerserviceBanner>
    {/if}

    <Tabs
      mxEdgeX
      tabs={[
        {
          name: $lang === "se" ? "Spottahaddi" : $lang === "en" ? "Spot Price" : "Spotpris",
          href: `${$lang ? `/${$lang}` : ``}/avtale${search}`,
        },
        {
          name: $lang === "se" ? "Fástahaddi" : $lang === "en" ? "Fixed Price" : "Fastpris",
          href: `${$lang ? `/${$lang}` : ``}/avtale/fastpris${search}`,
        },
      ]}
    >
      {@render children?.()}
    </Tabs>

    {#if !!$lang && data.page?.terminate_contract_href}
      <div class="mx-edge-x my-10 children-text-center">
        {#if data.page.terminate_contract_title}
          <h2>{data.page.terminate_contract_title}</h2>
        {/if}

        {#if data.page.terminate_contract_description}
          <p>{data.page.terminate_contract_description}</p>
        {/if}

        <p>
          <Button
            primary
            arrow
            target="_blank"
            class="button"
            href={data.page.terminate_contract_href}
          >
            {data.page.terminate_contract_link_label || data.page.terminate_contract_href}
          </Button>
        </p>
      </div>

      <hr />
    {/if}
  </LanguagePicker>

  <div class="mx-edge-x my-10">
    <div class="children-inline children-text-body text-center">
      <h2>Er du medlem i en av disse foreningene kan du få fordeler hos oss</h2>
      <p>– se hvilke fordelsavtaler vi tilbyr:</p>
    </div>

    <ul
      class="p-0 list-none flex gap-4 flex-wrap justify-center flex-col items-center tablet:flex-row"
    >
      <li>
        <Button arrow href="/avtale/seniorstrom{search}">Pensjonistforbundet</Button>
      </li>
      <li>
        <Button arrow href="/avtale/strawberry{search}">Strawberry</Button>
      </li>
      <li>
        <Button arrow href="/avtale/bonord{search}">Bonord</Button>
      </li>
      <li>
        <Button arrow href="/avtale/pbl{search}">Private Barnehagers Landsforbund</Button>
      </li>
      <li>
        <Button arrow href="/avtale/plusskunde{search}">Plusskunde</Button>
      </li>
      <li>
        <Button arrow href="/avtale/boligselskap{search}">Borettslag og sameier</Button>
      </li>
      <li>
        <Button arrow href="/avtale/landbruk{search}">Landbruk</Button>
      </li>
    </ul>
  </div>

  <p class="px-edge-x my-10 text-center">
    <Button arrow link class="font-normal underline" href="/alle-avtaler"
      >Se prisliste over alle våre avtaler</Button
    >
  </p>

  {#if app_teaser}
    <TeaserSection class="my-12" circleImage={false} teaser={app_teaser} href="/app" />
  {/if}
{:else}
  {@render children?.()}
{/if}
